import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip,
  CircularProgress, Alert,
} from "@mui/material";
import { getMarketplaceListing, importFromMarketplace } from "../api/backend";

const TYPE_LABELS = { TEST: "Test", SKILL_SHEET: "Skill sheet", DOCUMENT: "Document", CLASS_TEMPLATE: "Class template" };

/**
 * The marketplace listing behind a Knowledge Bank item: what it is now, which version the tenant
 * has, and (for an imported copy) a button to pull the current marketplace version into the copy.
 * `onUpdated` fires after a successful update so the caller can reload its list.
 */
export default function MarketplaceListingDialog({ listingId, onClose, onUpdated }) {
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setListing(null); setError(null); setConfirming(false);
    if (!listingId) return undefined;
    let live = true;
    getMarketplaceListing(listingId)
      .then((l) => { if (live) setListing(l); })
      .catch((e) => { if (live) setError(e?.response?.data?.message || "Couldn't load this marketplace listing."); });
    return () => { live = false; };
  }, [listingId]);

  const hasCopy = listing?.importedItemId != null;
  const newer = hasCopy && listing.sourceContentVersion > listing.importedAtVersion;
  const withdrawn = listing && listing.status !== "PUBLISHED_GLOBAL";

  const update = async () => {
    setBusy(true); setError(null);
    try {
      await importFromMarketplace(listing.id, listing.itemType);
      onUpdated && onUpdated(listing);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || "Update failed.");
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <Dialog open={!!listingId} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Marketplace listing</DialogTitle>
      <DialogContent>
        {!listing && !error && <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {listing && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="h6">{listing.title}</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip size="small" label={TYPE_LABELS[listing.itemType] || listing.itemType} />
              {listing.category && <Chip size="small" variant="outlined" label={listing.category} />}
              <Chip size="small" label={`Marketplace version ${listing.sourceContentVersion}`} />
              {hasCopy && <Chip size="small" variant="outlined" label={`Your copy is from version ${listing.importedAtVersion}`} />}
              {listing.own && <Chip size="small" color="primary" label="Your listing" />}
              {withdrawn && <Chip size="small" color="warning" label="Not currently listed" />}
            </Box>
            {listing.description && <Typography color="text.secondary">{listing.description}</Typography>}
            {listing.updatedAt && (
              <Typography variant="caption" color="text.secondary">
                Last updated {new Date(listing.updatedAt).toLocaleDateString()}
              </Typography>
            )}
            {hasCopy && !withdrawn && (newer
              ? <Alert severity="info">A newer version is on the marketplace. Updating replaces your copy's content with it; edits you made to your copy are overwritten.</Alert>
              : <Alert severity="success">Your copy is up to date with the marketplace.</Alert>)}
            {hasCopy && withdrawn && <Alert severity="warning">This listing was removed from the marketplace, so it can't be updated from there.</Alert>}
            {listing.own && <Alert severity="info">This is your training center's own listing. Update it from the item's Marketplace menu.</Alert>}
            {confirming && (
              <Alert severity="warning">Overwrite your copy with marketplace version {listing.sourceContentVersion}? This can't be undone.</Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        {newer && !withdrawn && !confirming && (
          <Button variant="contained" onClick={() => setConfirming(true)}>Update from marketplace</Button>
        )}
        {newer && !withdrawn && confirming && (
          <Button variant="contained" color="warning" onClick={update} disabled={busy}>
            {busy ? "Updating…" : "Yes, overwrite my copy"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
